import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { handleError } from "../../../../utils/ErrorHandler";

const initialState = {
  list: [],
  franchise: null,
  error: "",
  isFetchingFranchises: false,
  isFetchingSingle: false,
  isSubmitting: false,
  isDeleting: false, 
};

// Get all franchises
export const getAllFranchises = createAsyncThunk(
  "franchise/getAllFranchises",
  async ({ status }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`user?status=${status}`);
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to fetch all franchises");
      return rejectWithValue(errorMessage);
    }
  }
);

// Get franchise by ID
export const getFranchiseById = createAsyncThunk(
  "franchise/getFranchiseById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`user/${id}`);
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to fetch franchise details");
      return rejectWithValue(errorMessage);
    }
  }
);

// Create franchise (Override Content-Type for multipart/form-data)
export const createFranchise = createAsyncThunk(
  "franchise/createFranchise",
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`user/register`, values, {
        headers: {
          "Content-Type": "multipart/form-data", // Override default
        },
      });
      toast.success("Franchise created successfully!");
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to create franchise");
      return rejectWithValue(errorMessage);
    }
  }
);

// Update franchise
export const updateFranchise = createAsyncThunk(
  "franchise/updateFranchise",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`user/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data", // Override default
        },
      });
      toast.success(data.message || "Franchise updated successfully!");
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to update franchise");
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete franchise
export const deleteFranchise = createAsyncThunk(
  "franchise/deleteFranchise",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.patch(`user/${id}`, { status: false });
      toast.success("Franchise deleted successfully!");
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to delete franchise");
      return rejectWithValue(errorMessage);
    }
  }
);

const franchiseSlice = createSlice({
  name: "franchise",
  initialState,
  reducers: {
    clearFranchise: (state) => {
      state.franchise = null;
    },
  },
  extraReducers: (builder) => {
    // getAllFranchises
    builder.addCase(getAllFranchises.pending, (state) => {
      state.isFetchingFranchises = true;
      state.error = "";
    });
    builder.addCase(getAllFranchises.fulfilled, (state, action) => {
      state.isFetchingFranchises = false;
      state.list = action.payload;
    });
    builder.addCase(getAllFranchises.rejected, (state, action) => {
      state.isFetchingFranchises = false;
      state.error = action.payload;
    });

    // getFranchiseById
    builder.addCase(getFranchiseById.pending, (state) => {
      state.isFetchingSingle = true;
      state.error = "";
    });
    builder.addCase(getFranchiseById.fulfilled, (state, action) => {
      state.isFetchingSingle = false;
      state.franchise = action.payload;
    });
    builder.addCase(getFranchiseById.rejected, (state, action) => {
      state.isFetchingSingle = false;
      state.error = action.payload;
    });

    // createFranchise
    builder.addCase(createFranchise.pending, (state) => {
      state.isSubmitting = true;
      state.error = "";
    });
    builder.addCase(createFranchise.fulfilled, (state, action) => {
      state.isSubmitting = false;
      state.list.push(action.payload);
    });
    builder.addCase(createFranchise.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = action.payload;
    });

    // updateFranchise
    builder.addCase(updateFranchise.pending, (state) => {
      state.isSubmitting = true;
      state.error = "";
    });
    builder.addCase(updateFranchise.fulfilled, (state, action) => {
      state.isSubmitting = false;
      const updatedFranchise = action.payload;
      state.list = state.list.map((franchise) =>
        franchise.id === updatedFranchise.id ? updatedFranchise : franchise
      );
      state.franchise = updatedFranchise;
    });
    builder.addCase(updateFranchise.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = action.payload;
    });

    // deleteFranchise
    builder.addCase(deleteFranchise.pending, (state) => {
      state.isDeleting = true;
      state.error = "";
    });
    builder.addCase(deleteFranchise.fulfilled, (state, action) => {
      state.isDeleting = false;
      state.list = state.list.filter(
        (franchise) => franchise.id !== action.meta.arg
      );
    });
    builder.addCase(deleteFranchise.rejected, (state, action) => {
      state.isDeleting = false;
      state.error = action.payload;
    });
  },
});

export default franchiseSlice.reducer;
export const { clearFranchise } = franchiseSlice.actions;